/**
 * 戦闘力カスタムビュー (PDG TE) の API 契約 — フロント/バック共有の単一 SoT。
 *
 * リクエスト/レスポンスの Zod schema と推論型をここに集約し、
 * - API の OpenAPI schema (`apps/api/product/.../combat-power/api/schemas.ts`)
 * - API の Firestore 読み取り検証 (`infrastructure/firestore-custom-views-repository.ts`)
 * - Web の API クライアント/フック (`apps/web/product-innovation-lab/.../useCombatPower.ts`)
 * がすべてこの 1 ファイルから import する。契約が二重定義されないようにするため
 * (architecture.md「API クライアント契約」: 自前 API の req/res 型は package 側を単一 SoT とする)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力カスタムビュー API 契約 (Zod schema + 推論型) の単一 SoT
 * @graph-connects none
 */
import { z } from 'zod';
/** 保存できるビュー数の上限。画面は値を写さず、超過時は API のエラーをそのまま見せる。@graph-connects none */
export const MAX_COMBAT_POWER_CUSTOM_VIEWS = 10;
/** 保存・取得される 1 ビュー (メタ情報込み)。@graph-connects none */
export const combatPowerCustomViewSchema = z.object({
    /** ビュー識別子 (クライアント生成値。旧固定スロット view-1〜3 も有効) */
    id: z.string(),
    /** 表示ラベル */
    name: z.string(),
    /** このビューに含めるメンバーの担当者名 (combat-power の members[].name と一致) */
    memberNames: z.array(z.string()),
    /** 最終更新者メール (未保存時 null) */
    updatedBy: z.string().nullable(),
    /** 最終更新日時 ISO 文字列 (未保存時 null) */
    updatedAt: z.string().nullable(),
});
/** `GET /combat-power/custom-views` レスポンス。@graph-connects none */
export const combatPowerCustomViewsResponseSchema = z.object({
    views: z.array(combatPowerCustomViewSchema),
    /** 閲覧ユーザーが編集権限を持つか (canEditCombatPowerExpected と同基準) */
    canEdit: z.boolean(),
});
/** 保存時に送る 1 ビュー (メタ情報はサーバが付与)。@graph-connects none */
export const combatPowerCustomViewInputSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(40),
    memberNames: z.array(z.string().min(1).max(80)).max(200),
});
/** `PUT /combat-power/custom-views` リクエスト (全件置換, 0 件で全削除)。@graph-connects none */
export const putCombatPowerCustomViewsRequestSchema = z.object({
    views: z.array(combatPowerCustomViewInputSchema).max(MAX_COMBAT_POWER_CUSTOM_VIEWS),
});
/**
 * Firestore に保存される文書本体。読み取り境界で実データを検証する用途。
 * @graph-connects none
 */
export const combatPowerCustomViewsDocumentSchema = z.object({
    views: z.array(combatPowerCustomViewSchema),
});
//# sourceMappingURL=combat-power-custom-views.js.map